import { MantineProvider, type MantineThemeOverride } from "@mantine/core";
import { Notifications } from "@mantine/notifications";
import { NextIntlClientProvider } from "next-intl";
import type { ReactNode } from "react";

import { ClarityScript } from "../integrations/ClarityScript";

export type AppRootProps = {
  locale: string;
  /** next-intl messages resolved server-side via `getMessages()`. */
  messages: Record<string, unknown>;
  /** className from a `next/font` instance (e.g. `Inter({...}).className`). */
  fontClassName: string;
  /** Mantine theme for this app. */
  theme: MantineThemeOverride;
  /**
   * Visual identity of the resolved brand, written to `<html data-brand>`.
   * Apps that serve several brands from one deployment use it to scope their
   * CSS color tokens. Omitted → the app's default `:root` tokens apply.
   */
  dataBrand?: string;
  /**
   * Microsoft Clarity project id, usually `clientEnv.CLARITY_PROJECT_ID`.
   * Omitted/empty → Clarity does not load (no-op until set in Vercel).
   */
  clarityProjectId?: string;
  /**
   * Optional wrapper rendered between `MantineProvider` and the inner
   * providers. Use it to add app-specific providers like `<ModalsProvider>`
   * without forcing every app to depend on `@mantine/modals`.
   */
  innerWrap?: (children: ReactNode) => ReactNode;
  children: ReactNode;
};

/**
 * Shared root layout shell — renders `<html>/<body>` plus the universal
 * provider stack (Mantine + Notifications + next-intl).
 *
 * Each app's `app/[locale]/layout.tsx` keeps the bits that legitimately
 * differ (CSS imports, font choice, brand-derived metadata) and delegates
 * the rest here.
 */
export function AppRoot({
  locale,
  messages,
  fontClassName,
  theme,
  dataBrand,
  clarityProjectId,
  innerWrap,
  children,
}: AppRootProps) {
  const inner = (
    <>
      {/* Above the modal layer (Mantine defaults notifications to 200, and the
          feedback modal sits at 1000) — an error toast raised by a modal has to
          clear that modal's overlay or it renders dimmed and unreadable. Crisp
          sits far below, at 100 (CrispChatWidget, GD-116). */}
      <Notifications position="bottom-right" zIndex={1000010} />
      <NextIntlClientProvider messages={messages}>
        {children}
      </NextIntlClientProvider>
    </>
  );

  return (
    <html lang={locale} data-brand={dataBrand} suppressHydrationWarning>
      <body className={fontClassName}>
        <ClarityScript projectId={clarityProjectId} />
        <MantineProvider theme={theme} defaultColorScheme="light">
          {innerWrap ? innerWrap(inner) : inner}
        </MantineProvider>
      </body>
    </html>
  );
}
